import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import EventLayout from "@/components/event/EventLayout";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { setVolunteerSession, getVolunteerToken } from "@/lib/eventHub";
import { Lock, Loader2, ShieldCheck } from "lucide-react";

export default function VolunteerLogin() {
  const navigate = useNavigate();
  const [pin, setPin] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    // If already logged in as volunteer or admin, redirect
    if (getVolunteerToken()) {
      navigate("/event/volunteer-dashboard", { replace: true });
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (data?.is_admin) {
          // Admin session works for dashboard too
          navigate("/event/volunteer-dashboard", { replace: true });
        }
      } catch { /* not logged in as admin, that's fine */ }
    })();
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!pin) { toast.error("Enter your event PIN"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/event/volunteer/pin-login", {
        pin: pin.trim(),
        volunteer_name: name.trim(),
      });
      setVolunteerSession(data.session_token, data.volunteer_name);
      toast.success(`Welcome ${data.volunteer_name}!`);
      navigate("/event/volunteer-dashboard", { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Login failed. Please check your PIN.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <EventLayout showProgramBanner={false}>
      <div className="mx-auto max-w-md px-5 py-12 min-h-[70vh] flex flex-col justify-center">
        <div className="text-center mb-8">
          <div className="mx-auto h-16 w-16 rounded-2xl bg-emerald-950 text-white flex items-center justify-center mb-4">
            <ShieldCheck size={30} />
          </div>
          <h1 className="font-[Cormorant_Garamond] text-3xl leading-tight text-emerald-950">Volunteer Login</h1>
          <p className="font-[Outfit] text-sm text-emerald-950/60 mt-2">
            Enter your event PIN to access the check-in dashboard.
          </p>
        </div>

        <form onSubmit={submit} data-testid="volunteer-login-form" className="space-y-3">
          <input
            data-testid="volunteer-login-name"
            type="text"
            placeholder="Your name (optional)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="name"
            className="w-full h-12 rounded-xl border-2 border-emerald-950/10 bg-white px-3 font-[Outfit] text-sm focus:border-fuchsia-500 outline-none"
          />
          <div className="relative">
            <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-emerald-950/40" />
            <input
              data-testid="volunteer-login-pin"
              type="password"
              inputMode="text"
              placeholder="Event PIN"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              autoFocus
              className="w-full h-14 rounded-xl border-2 border-emerald-950/10 bg-white pl-11 pr-4 font-[Outfit] text-base font-bold tracking-widest text-emerald-950 focus:border-fuchsia-500 outline-none"
            />
          </div>
          <Button
            type="submit" disabled={loading}
            data-testid="volunteer-login-submit"
            className="w-full h-14 rounded-xl bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-[Outfit] font-bold text-base"
          >
            {loading ? <Loader2 className="animate-spin" size={18} /> : "Sign In"}
          </Button>
          {error && (
            <div
              data-testid="volunteer-pin-error"
              role="alert"
              className="rounded-xl border-2 border-fuchsia-300 bg-fuchsia-50 px-3 py-2 text-sm font-[Outfit] font-semibold text-fuchsia-800"
            >
              {error}
            </div>
          )}
        </form>

        <div className="mt-8 text-center text-xs font-[Outfit] text-emerald-950/60">
          Event managers with Google accounts can also{" "}
          <Link to="/admin" data-testid="volunteer-login-admin-link" className="text-fuchsia-700 font-semibold hover:underline">
            sign in with Google
          </Link>.
        </div>
      </div>
    </EventLayout>
  );
}
